import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './App.css';

function ArticlePage({ isDarkMode }) {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/articles.json')
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(articles => {
        const foundArticle = articles.find(a => a.slug === slug);
        if (!foundArticle) {
          setError('Article not found');
        } else {
          setArticle(foundArticle);
        }
      })
      .catch(err => {
        console.error('Error loading article:', err);
        setError('Failed to load article');
      });
  }, [slug]);

  if (error) return <div className="error">{error}</div>;
  if (!article) return <div>Loading...</div>;

  const description = article.text.length > 150 ? article.text.substring(0, 150) + '...' : article.text;
  const articleUrl = `https://hb.news/articles/${slug}`;
  const thumbnailUrl = `https://hb.news${article.thumbnail.replace('public/', '/')}`;

  const paragraphs = article.text.split('\n').filter(p => p.trim() !== '');

  return (
    <div className={`article-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <Helmet>
        <title>{article.title} | HB.NEWS</title>
        <meta name="description" content={description} data-react-helmet="true" />
        <meta property="og:title" content={article.title} data-react-helmet="true" />
        <meta property="og:description" content={description} data-react-helmet="true" />
        <meta property="og:image" content={thumbnailUrl} data-react-helmet="true" />
        <meta property="og:url" content={articleUrl} data-react-helmet="true" />
        <meta property="og:type" content="article" data-react-helmet="true" />
        <meta name="twitter:card" content="summary_large_image" data-react-helmet="true" />
        <meta name="twitter:title" content={article.title} data-react-helmet="true" />
        <meta name="twitter:description" content={description} data-react-helmet="true" />
        <meta name="twitter:image" content={thumbnailUrl} data-react-helmet="true" />
      </Helmet>
      <h1>{article.title}</h1>
      <p className="meta">By {article.author} | {new Date(article.date).toLocaleDateString()}</p>
      <img src={article.thumbnail.replace('public/', '/')} alt={article.title} className="article-image" />
      <div className="article-text">
        {paragraphs.map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
      </div>
      <Link to="/" className="back-link">Back to Home</Link>
    </div>
  );
}

function NotFoundPage({ isDarkMode }) {
  return (
    <div className={`not-found ${isDarkMode ? 'dark-mode' : ''}`}>
      <h1>404 - Page Not Found</h1>
      <p>The page you're looking for doesn't exist.</p>
      <Link to="/" className="back-link">Back to Home</Link>
    </div>
  );
}

function HomePage({ isDarkMode, toggleDarkMode, toggleMenu, closeMenu, isMenuOpen, menuRef }) {
  const [stories, setStories] = useState([]);
  const [visibleStories, setVisibleStories] = useState(20);
  const storiesPerLoad = 20;

  useEffect(() => {
    fetch('/stories.json')
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        console.log('Fetched stories:', data);
        setStories(data);
      })
      .catch(err => console.error('Error loading stories:', err));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuRef, closeMenu]);

  const loadMore = () => {
    console.log('Loading more stories... Current visible:', visibleStories);
    setVisibleStories(prev => {
      const newVisible = prev + storiesPerLoad;
      console.log('New visible stories:', newVisible);
      return newVisible;
    });
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // Function to adjust the image path for Netlify
  const getImageUrl = (imagePath) => {
    if (imagePath && imagePath.startsWith('public/')) {
      return imagePath.replace('public/', '/');
    }
    return imagePath;
  };

  return (
    <div className="main-content">
      <Helmet>
        <meta name="description" content="Latest news from Huntington Beach, aggregated for you." data-react-helmet="true" />
        <meta property="og:title" content="HB.NEWS" data-react-helmet="true" />
        <meta property="og:description" content="Latest news from Huntington Beach, aggregated for you." data-react-helmet="true" />
        <meta property="og:image" content="https://hb.news/images/logo.png" data-react-helmet="true" />
        <meta property="og:url" content="https://hb.news/" data-react-helmet="true" />
        <meta property="og:type" content="website" data-react-helmet="true" />
        <meta name="twitter:card" content="summary_large_image" data-react-helmet="true" />
        <meta name="twitter:title" content="HB.NEWS" data-react-helmet="true" />
        <meta name="twitter:description" content="Latest news from Huntington Beach, aggregated for you." data-react-helmet="true" />
        <meta name="twitter:image" content="https://hb.news/images/logo.png" data-react-helmet="true" />
      </Helmet>
      <div id="stories">
        {stories.slice(0, visibleStories).map(story => (
          <div key={story.id} className="story">
            <a href={story.url} target={story.type === 'story' ? '_blank' : '_self'} rel={story.type === 'story' ? 'noopener noreferrer' : undefined}>
              {story.headline}
            </a>
            {story.image && (
              <div className="image-container">
                {story.type === 'article' && (
                  <span className="hbn-badge">HBN</span>
                )}
                <img
                  src={getImageUrl(story.image)}
                  alt={story.headline}
                  loading="lazy"
                  onError={(e) => {
                    console.log(`Failed to load image: ${e.target.src}`);
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      {visibleStories < stories.length && <button onClick={loadMore}>Load More</button>}
      <footer className="site-footer">
        <p>© {new Date().getFullYear()} HB.NEWS. All rights reserved.</p>
        <p><button onClick={scrollToTop} className="back-to-top">Back to Top</button></p>
      </footer>
    </div>
  );
}

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('darkMode', isDarkMode);
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <Router>
      <div className={`App ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className="site-header">
          <div className="logo">
            <Link to="/">
              <img
                src={isDarkMode ? '/images/logoblack.png' : '/images/logo.png'}
                alt="HB.NEWS Logo"
              />
            </Link>
          </div>
          <ul className="social-links">
            <li>
              <a href="https://twitter.com/_hbnews" target="_blank" rel="noopener noreferrer">
                <img
                  src={isDarkMode ? '/images/x-icon-white.svg' : '/images/x-icon.svg'}
                  alt="X (Twitter)"
                  className="social-icon"
                />
              </a>
            </li>
            <li>
              <a href="https://instagram.com/surfcity.usa" target="_blank" rel="noopener noreferrer">
                <img
                  src={isDarkMode ? '/images/instagram-icon-white.svg' : '/images/instagram-icon.svg'}
                  alt="Instagram"
                  className="social-icon"
                />
              </a>
            </li>
            <li className="tools-menu" ref={menuRef}>
              <button onClick={toggleMenu} className="tools-btn">
                <img
                  src={isDarkMode ? '/images/tools-icon-white.svg' : '/images/tools-icon.svg'}
                  alt="Tools"
                  className="social-icon"
                />
              </button>
              {isMenuOpen && (
                <ul className="submenu">
                  <li>
                    <button onClick={toggleDarkMode} className="submenu-item">
                      {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                    </button>
                  </li>
                  <li>
                    <button onClick={closeMenu} className="submenu-close">✕</button>
                  </li>
                </ul>
              )}
            </li>
          </ul>
        </header>
        <Routes>
          <Route path="/" element={<HomePage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} toggleMenu={toggleMenu} closeMenu={closeMenu} isMenuOpen={isMenuOpen} menuRef={menuRef} />} />
          <Route path="/articles/:slug" element={<ArticlePage isDarkMode={isDarkMode} />} />
          <Route path="*" element={<NotFoundPage isDarkMode={isDarkMode} />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;